import { useSelector } from "react-redux";
import { useContext, useEffect, useRef, useState } from "react";

import { GlobalContext } from "../../../context/GlobalContext";

import { FaCheck, FaSearch, FaUser, FaWhatsappSquare } from "react-icons/fa";
import ReactInputMask from "react-input-mask";

import { Toast } from "../../../utils/Toast";
import { OrderContext } from "../../../context/OrderContext";
import { api } from "../../../api/api";
import { Btn } from "../../Btn";

const noop = () => {}

export function CustomerForm({ onDirtyChange = noop }) {
  const { isAccess } = useContext(GlobalContext)
  const order = useSelector(state => state.order.orderSelect)
  const { update } = useContext(OrderContext)
  const [draft, setDraft] = useState(order || {})
  const [isDirty, setIsDirty] = useState(false)
  const selectedOrderRef = useRef(order)
  const lookupRequestRef = useRef(0)

  selectedOrderRef.current = order

  useEffect(() => {
    setDraft(selectedOrderRef.current || {})
    setIsDirty(false)
    onDirtyChange(false)

    return () => {
      lookupRequestRef.current += 1
    }
  }, [order?.id, onDirtyChange])

  function markAsDirty() {
    if (!isDirty) {
      setIsDirty(true)
      onDirtyChange(true)
    }
  }

  async function searchCustomer() {
    if (!isAccess('pedidos', 'update')) {
      return Toast.warning('Você não tem permissão para atualizar pedido')
    }

    const phone = await Toast.getValue(
      'Buscar cadastro',
      'Digite o telefone do cliente',
      'Digite um telefone válido.',
      'body',
      'tel',
      () => value => {
        if (![10, 11].includes(changePhonesRemoveCaracter(value).length)) {
          return 'Digite um telefone com DDD.'
        }
      }
    )

    if (!phone) return

    const normalizedPhone = changePhonesRemoveCaracter(phone)
    const requestId = ++lookupRequestRef.current

    try {
      const response = await api.get('/customers/by-phone', { params: { phone: normalizedPhone } })

      if (requestId !== lookupRequestRef.current) return

      const customer = response.data?.data
      if (!customer) {
        return Toast.warning('Cadastro não encontrado para este telefone')
      }

      setDraft(currentDraft => ({
        ...currentDraft,
        idCustomer: customer.idCustomer ?? '',
        name: customer.name ?? currentDraft.name ?? '',
        cpf: customer.cpf ?? currentDraft.cpf ?? '',
        phone: customer.phone ?? normalizedPhone,
        address: customer.address ?? currentDraft.address ?? '',
        number: customer.number ?? currentDraft.number ?? '',
        complement: customer.complement ?? currentDraft.complement ?? '',
        referencePoint: customer.referencePoint ?? currentDraft.referencePoint ?? '',
        cep: customer.cep ?? currentDraft.cep ?? '',
        deliveryDistrict: {
          ...currentDraft.deliveryDistrict,
          district: customer.district ?? currentDraft.deliveryDistrict?.district ?? '',
        },
      }))
      markAsDirty()
    } catch (error) {
      Toast.warning('Não foi possível buscar o cadastro')
    }
  }

  function onChange(key, valueInput) {
    if (!isAccess('pedidos', 'update')) {
      return Toast.warning('Você não tem permissão para atualizar pedido')
    }

    if (key === 'deliveryDistrict') {
      valueInput = {
        ...draft.deliveryDistrict,
        district: valueInput,
      }
    }

    setDraft({
      ...draft,
      ...(key === 'phone' ? { idCustomer: '' } : {}),
      [key]: valueInput,
    })
    markAsDirty()
  }

  async function save() {
    if (!isAccess('pedidos', 'update')) {
      return Toast.warning('Você não tem permissão para atualizar pedido')
    }

    const saved = await update(false, true, draft)
    if (saved) {
      setIsDirty(false)
      onDirtyChange(false)
    }
  }

  function changePhonesRemoveCaracter(value = '') {
    return String(value).replace(/\D/g, '')
  }

  function openConversationWpp(phone = '') {
    if (!phone) {
      return
    }

    phone = phone.replace('(', '').replace(')', '').replace(/ /ig, '')

    window.open(`https://api.whatsapp.com/send?phone=55${phone}`, '_blank')
  }

  return (
    <div className="container order-customer-form">
      <div className="mt-3 mb-3 order-form-heading">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: '31px', height: '31px', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#ff620025', borderRadius: 10 }}>
            <FaUser size={15} color="#ff6200" />
          </div>

          <p className='text-gray' style={{ fontSize: 'var(--font-size-section-title)', fontWeight: '600' }}>Informações do Cliente</p>
        </div>

        <Btn
          text="Buscar cadastro"
          click={searchCustomer}
          icon={<FaSearch size={14} />}
          leftIcon
          width={170}
          height={36}
          isShadow={false}
        />
      </div>
      <div className="row mt-2 order-customer-fields">
        <div className="col-sm-6 mt-2">
          <label htmlFor="" className='text-gray' style={{ fontWeight: 500 }}>Nome</label>
          <input
            maxLength={100}
            value={draft?.name || ''}
            style={{ height: '30px', borderRadius: 8 }}
            onChange={(e) => onChange('name', e.target.value)}
            className="form-control form-control-sm" type="text"
          />
        </div>
        <div className="col-sm-3 mt-2">
          <label htmlFor="" className='text-gray' style={{ fontWeight: 500 }}>CPF/CNPJ</label>
          <ReactInputMask
            mask={String(draft?.cpf ?? '').replace(/\D/g, '').length > 11 ? "99.999.999/9999-99" : "999.999.999-99"}
            className="form-control form-control-sm"
            value={draft?.cpf ?? ''}
            style={{ height: '30px', borderRadius: 8 }}
            onChange={(e) => onChange('cpf', changePhonesRemoveCaracter(e.target.value))}
          />
        </div>
        <div className="col-sm-3 mt-2">
          <label htmlFor="" className='text-gray' style={{ fontWeight: 500 }}>Telefone <FaWhatsappSquare size={15} className="pointer" color="green" onClick={() => openConversationWpp(draft.phone)} /> </label>
          <ReactInputMask
            mask="(99) 99999-9999"
            aria-label="Telefone"
            className="form-control form-control-sm"
            value={draft?.phone ?? ''}
            style={{ height: '30px', borderRadius: 8 }}
            onChange={(e) => onChange('phone', changePhonesRemoveCaracter(e.target.value))}
          />
        </div>
        <div className="col-sm-6 mt-2">
          <label htmlFor="" className='text-gray' style={{ fontWeight: 500 }}>Endereço</label>
          <input
            maxLength={100}
            className="form-control form-control-sm"
            type="text"
            value={draft?.address || ''}
            style={{ height: '30px', borderRadius: 8 }}
            onChange={(e) => onChange('address', e.target.value)}
          />
        </div>
        <div className="col-sm-2 mt-2">
          <label htmlFor="" className='text-gray' style={{ fontWeight: 500 }}>Nº</label>
          <input
            maxLength={40}
            className="form-control form-control-sm"
            type="text"
            value={draft?.number || ''}
            style={{ height: '30px', borderRadius: 8 }}
            onChange={(e) => onChange('number', e.target.value)}
          />
        </div>
        <div className="col-sm-4 mt-2">
          <label htmlFor="" className='text-gray' style={{ fontWeight: 500 }}>Complemento</label>
          <input
            maxLength={50}
            className="form-control form-control-sm"
            type="text"
            value={draft?.complement || ''}
            style={{ height: '30px', borderRadius: 8 }}
            onChange={(e) => onChange('complement', e.target.value)}
          />
        </div>
        <div className="col-sm-5 mt-2">
          <label htmlFor="" className='text-gray' style={{ fontWeight: 500 }}>Bairro</label>
          <input
            maxLength={100}
            className="form-control form-control-sm"
            type="text"
            value={draft?.deliveryDistrict?.district || ''}
            style={{ height: '30px', borderRadius: 8 }}
            onChange={(e) => onChange('deliveryDistrict', e.target.value)}
          />
        </div>
        <div className="col-sm-3 mt-2">
          <label htmlFor="" className='text-gray' style={{ fontWeight: 500 }}>CEP</label>
          <input
            maxLength={10}
            className="form-control form-control-sm"
            type="text"
            value={draft?.cep || ''}
            style={{ height: '30px', borderRadius: 8 }}
            onChange={(e) => onChange('cep', e.target.value)}
          />
        </div>
        <div className="col-sm-4 mt-2">
          <label htmlFor="" className='text-gray' style={{ fontWeight: 500 }}>Referência</label>
          <input
            maxLength={50}
            className="form-control form-control-sm"
            type="text"
            value={draft?.referencePoint || ''}
            style={{ height: '30px', borderRadius: 8 }}
            onChange={(e) => onChange('referencePoint', e.target.value)}
          />
        </div>
        <div className="col-12 order-customer-actions">
          <Btn
            text="Salvar alterações"
            click={save}
            icon={<FaCheck size={14} color="white" />}
            leftIcon
            backgroundColor="#ff6200"
            hoverColor="#d55302"
            color="white"
            width={190}
            height={38}
            disabled={!isDirty}
          />
        </div>
      </div>
    </div>
  )
}
